import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { GET_FLOW_OPTIONS } from '../../../lib/graphql/queries/organizations';

interface GQLFlowSummary {
  flowId: string;
  name: string | null;
  flowType: string | null;
}

interface GetFlowOptionsData {
  flows: { flows: GQLFlowSummary[] };
}

const PAGE_SIZE = 250;

export interface FlowOption {
  /** Flow UUID — pass as the GQL `flowIds` filter value. */
  value: string;
  /** Display label, e.g. "Revive Q4 buyers (Revive)". */
  label: string;
  /** Canonical flow type (Revive / Engage / Convert / Flywheel). */
  flowType: string;
}

// Stable ordering for flow types in the dropdown so similar flows cluster.
// Unknown types sort last, alphabetically.
const FLOW_TYPE_ORDER = ['Revive', 'Engage', 'Convert', 'Flywheel'];

const flowTypeRank = (type: string): number => {
  const idx = FLOW_TYPE_ORDER.indexOf(type);
  return idx === -1 ? FLOW_TYPE_ORDER.length : idx;
};

/**
 * Returns flow dropdown options keyed by flow_id. Use this for filters that hit
 * the new `flowIds` GraphQL variable.
 *
 * Hits the slim `GET_FLOW_OPTIONS` query (3 fields) instead of `GET_FLOWS` so
 * filter modals don't pay for voicemail content / prompt params they never use.
 *
 * Options are grouped by flow type (Revive → Engage → Convert → Flywheel → other),
 * then sorted by name within each group.
 */
export const useOrgFlowOptions = (
  organizationId: string | undefined,
  enabled = true
) => {
  const { data, loading, error } = useQuery<GetFlowOptionsData>(GET_FLOW_OPTIONS, {
    variables: {
      organizationId: organizationId ?? '',
      limit: PAGE_SIZE,
      offset: 0,
      sortOrder: 'ASC',
      sortBy: 'name',
    },
    skip: !enabled || !organizationId,
    fetchPolicy: 'cache-first',
  });

  const options = useMemo<FlowOption[]>(() => {
    const flows = data?.flows?.flows ?? [];
    return flows
      .filter((f) => f.flowId && (f.name ?? '').trim())
      .map((f) => {
        const name = (f.name ?? '').trim();
        const type = (f.flowType ?? '').trim();
        return {
          value: f.flowId,
          label: type ? `${name} (${type})` : name,
          flowType: type,
        };
      })
      .sort((a, b) => {
        const rankDelta = flowTypeRank(a.flowType) - flowTypeRank(b.flowType);
        if (rankDelta !== 0) return rankDelta;
        // Within the same flow type, fall back to alpha on label for stability.
        return a.label.localeCompare(b.label);
      });
  }, [data]);

  const flowTypes = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const opt of options) {
      if (opt.flowType) set.add(opt.flowType);
    }
    return Array.from(set).sort((a, b) => flowTypeRank(a) - flowTypeRank(b));
  }, [options]);

  return { options, flowTypes, isLoading: loading, error };
};
